import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useBack, useWindowWidth } from '../hooks';
import { useToast } from '../context/ToastContext';
import { TEAL, AMBER, BORD, CARD, SUNK, DARK, MUTED, DIM, TEXT } from '../stockShared';
import { STATUS, statusChip, when } from '../components/SuggestPanel';

// Suggestions (the director, 28 Oct 2026; migration 084): what operators and
// setters sent from the Suggest button -- a picture of the page, circled
// where they mean, and what should change. Mark each Seen, Done or Not doing
// and reply; the sender's bell hears of it ("the employees then see the
// director is actually listening to them").

const ANSWERS = ['seen', 'done', 'not_doing'];

export default function AdminSuggestions() {
  const goBack = useBack('/admin');
  const { toast } = useToast();
  const phone = useWindowWidth() < 760;
  const [params] = useSearchParams();
  const lit = Number(params.get('id')) || null;
  const [show, setShow] = useState('open');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.get('/api/v1/suggestions', { params: { show } })
      .then(r => { setData(r.data); setError(null); })
      .catch(err => setError(err.response?.data?.error || 'The suggestions could not be read just now. Check the connection and reload.'));
  }, [show]);
  useEffect(() => { load(); }, [load]);

  const replace = (s) => setData(d => ({ ...d, suggestions: d.suggestions.map(x => (x.id === s.id ? s : x)) }));
  const open = data ? data.counts.new + data.counts.seen : 0;
  const all = data ? Object.values(data.counts).reduce((a, b) => a + b, 0) : 0;
  const seg = (k, label) => (
    <button type="button" role="tab" aria-selected={show === k} onClick={() => setShow(k)}
      style={{ background: show === k ? CARD : 'transparent', border: 'none', boxShadow: show === k ? `0 0 0 1px ${BORD}` : 'none', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 700, color: show === k ? TEXT : DIM, cursor: 'pointer', fontFamily: 'system-ui' }}>{label}</button>
  );

  return (
    <div style={{ padding: phone ? '14px 12px 90px' : '20px 24px 60px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box', maxWidth: 1180, margin: '0 auto' }}>
      <button onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 8 }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: 10, marginRight: 4 }} />Back
      </button>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>Suggestions</h1>
      <p style={{ color: DIM, fontSize: 13, margin: '0 0 14px', maxWidth: 760, lineHeight: 1.5 }}>
        What operators and setters sent with the Suggest button: a picture of the page, circled where they mean, and what should change. Mark each one and reply; the person who sent it sees your answer on their bell.
      </p>
      <div role="tablist" style={{ display: 'inline-flex', background: SUNK, border: `1px solid ${BORD}`, borderRadius: 10, padding: 3, marginBottom: 16 }}>
        {seg('open', `Open · ${open}`)}{seg('all', `All · ${all}`)}
      </div>
      {error && <div style={{ color: AMBER, fontSize: 13 }}>{error}</div>}
      {!data && !error && <div style={{ color: MUTED, fontSize: 13 }}>Reading…</div>}
      {data && !data.suggestions.length && (
        <div style={{ color: DIM, fontSize: 13, background: CARD, border: `1px solid ${BORD}`, borderRadius: 12, padding: 16 }}>
          {show === 'open' ? (all ? 'Every suggestion is answered. All shows them.' : 'Nothing yet. Operators and setters send them with the Suggest button beside the bell.') : 'Nothing yet.'}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(data?.suggestions || []).map(s => <Card key={s.id} s={s} lit={lit === s.id} phone={phone} toast={toast} onSaved={replace} />)}
      </div>
    </div>
  );
}

function Card({ s, lit, phone, toast, onSaved }) {
  const [pic, setPic] = useState(null);
  const [reply, setReply] = useState(s.reply || '');
  const [editing, setEditing] = useState(!s.reply);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!s.has_picture) return;
    api.get(`/api/v1/suggestions/${s.id}/picture`).then(r => setPic(r.data.url)).catch(() => setPic(false));
  }, [s.id, s.has_picture]);
  useEffect(() => { if (lit && ref.current) ref.current.scrollIntoView({ block: 'center' }); }, [lit]);

  async function answer(body, said) {
    setSaving(true);
    try {
      const { data } = await api.put(`/api/v1/suggestions/${s.id}`, body);
      onSaved(data);
      if (body.reply !== undefined) setEditing(false);
      if (data.changed) toast.success(`${said} ${s.sent_by || 'The sender'} sees it on their bell.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'It could not be saved just now. Check the connection and try again.');
    }
    setSaving(false);
  }
  async function bigger() {
    try { const { data } = await api.get(`/api/v1/suggestions/${s.id}/picture`); window.open(data.url, '_blank', 'noopener'); }
    catch (err) { toast.error(err.response?.data?.error || 'The picture could not be opened just now.'); }
  }

  const btn = (on, color) => ({ background: on ? `${color}24` : 'transparent', color: on ? color : DIM, border: `1px solid ${on ? color : '#374151'}`, borderRadius: 8, padding: '5px 11px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui' });
  return (
    <article ref={ref} style={{ background: CARD, border: `1px solid ${lit ? TEAL : BORD}`, borderRadius: 12, padding: 14, display: 'grid', gridTemplateColumns: phone ? '1fr' : '300px minmax(0,1fr)', gap: 14 }}>
      <div>
        {s.has_picture ? (
          pic ? (
            <button type="button" onClick={bigger} title="Open the picture full size" style={{ padding: 0, border: `1px solid ${BORD}`, borderRadius: 8, background: SUNK, cursor: 'zoom-in', display: 'block', width: '100%' }}>
              <img src={pic} alt={`The page ${s.sent_by || 'they'} saw: ${s.page_title || s.page_path}`} style={{ width: '100%', display: 'block', borderRadius: 8 }} />
            </button>
          ) : <div style={{ aspectRatio: '16 / 10', background: SUNK, borderRadius: 8, display: 'grid', placeItems: 'center', fontSize: 12, color: MUTED }}>{pic === false ? 'The picture could not be read.' : 'Reading the picture…'}</div>
        ) : <div style={{ aspectRatio: '16 / 10', background: SUNK, borderRadius: 8, display: 'grid', placeItems: 'center', fontSize: 12, color: MUTED }}>Sent without a picture</div>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, color: DIM }}>
          {statusChip(s.status)}
          <span><b style={{ color: TEXT }}>{s.sent_by || 'Someone'}</b> · {when(s.created_at)}</span>
          <span>· from <Link to={s.page_path} style={{ color: TEAL }}>{s.page_title || s.page_path}</Link></span>
        </div>
        <div style={{ fontSize: 14.5, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{s.comment}</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {ANSWERS.map(k => (
            <button key={k} type="button" disabled={saving} aria-pressed={s.status === k} onClick={() => s.status !== k && answer({ status: k }, `Marked ${STATUS[k].label.toLowerCase()}.`)} style={btn(s.status === k, STATUS[k].color)}>{STATUS[k].label}</button>
          ))}
          {s.status_at && <span style={{ fontSize: 12, color: MUTED }}>{s.status_by ? `${s.status_by}, ` : ''}{when(s.status_at)}</span>}
        </div>
        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor={`reply-${s.id}`} style={{ fontSize: 12.5, fontWeight: 700 }}>Reply to {s.sent_by || 'them'}</label>
            <textarea id={`reply-${s.id}`} value={reply} onChange={e => setReply(e.target.value)} rows={2} maxLength={2000} placeholder="e.g. Good catch: changed it today."
              style={{ background: DARK, border: `1px solid ${BORD}`, borderRadius: 8, color: TEXT, padding: '7px 10px', fontSize: 13.5, fontFamily: 'system-ui', resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" disabled={saving || !reply.trim()} onClick={() => answer({ reply }, 'Reply sent.')}
                style={{ background: TEAL, color: '#0f172a', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 800, cursor: 'pointer', opacity: saving || !reply.trim() ? 0.5 : 1, fontFamily: 'system-ui' }}>Send reply</button>
              {s.reply && <button type="button" onClick={() => { setReply(s.reply); setEditing(false); }} style={{ ...btn(false, DIM) }}>Cancel</button>}
            </div>
          </div>
        ) : (
          <div style={{ background: SUNK, border: `1px solid ${BORD}`, borderRadius: 8, padding: '8px 11px', fontSize: 13.5, lineHeight: 1.45 }}>
            <span style={{ color: TEAL, fontWeight: 700 }}>{s.replied_by || 'You'}:</span> <span style={{ whiteSpace: 'pre-wrap' }}>{s.reply}</span>
            <span style={{ color: MUTED, fontSize: 12 }}> · {s.replied_at ? when(s.replied_at) : ''} · </span>
            <button type="button" onClick={() => setEditing(true)} style={{ background: 'none', border: 'none', color: DIM, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 12 }}>Change the reply</button>
          </div>
        )}
      </div>
    </article>
  );
}
